import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function ClientsTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  // Mock client data with added fields for status, application stage, and last contact date
  const clients = [{
    id: '1',
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    phone: '(555) 987-6543',
    applications: 3,
    status: 'Active',
    applicationStage: 'Funded',
    lastContactDate: 'May 15, 2023'
  }, {
    id: '2',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '(555) 123-4567',
    applications: 1,
    status: 'Lead',
    applicationStage: 'Pre-approval',
    lastContactDate: 'Jun 2, 2023'
  }, {
    id: '3',
    name: 'Emily Johnson',
    email: 'emily.johnson@example.com',
    phone: '(555) 234-5678',
    applications: 2,
    status: 'Active',
    applicationStage: 'Underwriting',
    lastContactDate: 'Apr 28, 2023'
  }, {
    id: '4',
    name: 'Michael Brown',
    email: 'michael.brown@example.com',
    phone: '(555) 345-6789',
    applications: 4,
    status: 'Active',
    applicationStage: 'Approved',
    lastContactDate: 'May 30, 2023'
  }, {
    id: '5',
    name: 'Sarah Williams',
    email: 'sarah.williams@example.com',
    phone: '(555) 456-7890',
    applications: 1,
    status: 'Closed',
    applicationStage: 'Funded',
    lastContactDate: 'Feb 15, 2023'
  }, {
    id: '6',
    name: 'David Martinez',
    email: 'david.martinez@example.com',
    phone: '(555) 567-8901',
    applications: 2,
    status: 'Active',
    applicationStage: 'Underwriting',
    lastContactDate: 'Jun 10, 2023'
  }, {
    id: '7',
    name: 'Jessica Taylor',
    email: 'jessica.taylor@example.com',
    phone: '(555) 678-9012',
    applications: 0,
    status: 'Lead',
    applicationStage: 'Pre-approval',
    lastContactDate: 'Jun 5, 2023'
  }, {
    id: '8',
    name: 'Robert Anderson',
    email: 'robert.anderson@example.com',
    phone: '(555) 789-0123',
    applications: 3,
    status: 'Active',
    applicationStage: 'Funded',
    lastContactDate: 'May 22, 2023'
  }, {
    id: '9',
    name: 'Lisa Garcia',
    email: 'lisa.garcia@example.com',
    phone: '(555) 890-1234',
    applications: 1,
    status: 'Declined',
    applicationStage: 'Declined',
    lastContactDate: 'Apr 12, 2023'
  }, {
    id: '10',
    name: 'Kevin Wilson',
    email: 'kevin.wilson@example.com',
    phone: '(555) 901-2345',
    applications: 2,
    status: 'Active',
    applicationStage: 'Approved',
    lastContactDate: 'May 18, 2023'
  }, {
    id: '11',
    name: 'Amanda Thomas',
    email: 'amanda.thomas@example.com',
    phone: '(555) 012-3456',
    applications: 5,
    status: 'Active',
    applicationStage: 'Funded',
    lastContactDate: 'Jun 1, 2023'
  }, {
    id: '12',
    name: 'Daniel Lee',
    email: 'daniel.lee@example.com',
    phone: '(555) 123-4567',
    applications: 1,
    status: 'Lead',
    applicationStage: 'Pre-approval',
    lastContactDate: 'Jun 8, 2023'
  }, {
    id: '13',
    name: 'Nicole White',
    email: 'nicole.white@example.com',
    phone: '(555) 234-5678',
    applications: 2,
    status: 'Active',
    applicationStage: 'Underwriting',
    lastContactDate: 'May 25, 2023'
  }, {
    id: '14',
    name: 'Christopher Harris',
    email: 'christopher.harris@example.com',
    phone: '(555) 345-6789',
    applications: 0,
    status: 'Closed',
    applicationStage: 'Funded',
    lastContactDate: 'Mar 10, 2023'
  }, {
    id: '15',
    name: 'Stephanie Clark',
    email: 'stephanie.clark@example.com',
    phone: '(555) 456-7890',
    applications: 3,
    status: 'Active',
    applicationStage: 'Approved',
    lastContactDate: 'May 20, 2023'
  }];
  // Pagination logic
  const indexOfLastClient = currentPage * itemsPerPage;
  const indexOfFirstClient = indexOfLastClient - itemsPerPage;
  const currentClients = clients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(clients.length / itemsPerPage);
  const paginate = pageNumber => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  // Helper function to render status with appropriate styling
  const renderStatus = status => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    switch (status) {
      case 'Active':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Lead':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'Closed':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        break;
      case 'Declined':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`} data-id="element-1276">
        {status}
      </span>;
  };
  // Helper function to render application stage with appropriate styling
  const renderApplicationStage = stage => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    switch (stage) {
      case 'Pre-approval':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Underwriting':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'Approved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Funded':
        bgColor = 'bg-purple-100';
        textColor = 'text-purple-800';
        break;
      case 'Declined':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`} data-id="element-1277">
        {stage}
      </span>;
  };
  return <div data-id="element-1278">
      <div className="flex justify-between items-center mb-6" data-id="element-1279">
        <h1 className="text-2xl font-bold" data-id="element-1280">Clients</h1>
        <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" data-id="element-1281">
          Add New Client
        </button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1282">
        <table className="min-w-full divide-y divide-neutral-200" data-id="element-1283">
          <thead className="bg-neutral-50" data-id="element-1284">
            <tr data-id="element-1285">
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1286">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1287">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1288">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1289">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1290">
                Most Recent Application Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1291">
                Last Contact Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1292">
                Applications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-1293">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200" data-id="element-1294">
            {currentClients.map(client => <tr key={client.id} data-id="element-1295">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1296">
                  <div className="text-sm font-medium text-neutral-900" data-id="element-1297">
                    {client.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1298">
                  <div className="text-sm text-neutral-500" data-id="element-1299">{client.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1300">
                  <div className="text-sm text-neutral-500" data-id="element-1301">{client.phone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1302">
                  {renderStatus(client.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1303">
                  {renderApplicationStage(client.applicationStage)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1304">
                  <div className="text-sm text-neutral-500" data-id="element-1305">
                    {client.lastContactDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1306">
                  <div className="text-sm text-neutral-500" data-id="element-1307">
                    {client.applications}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500" data-id="element-1308">
                  <Link to={`/clients/${client.id}`} className="text-blue-600 hover:text-blue-900" data-id="element-1309">
                    View
                  </Link>
                </td>
              </tr>)}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex items-center justify-between" data-id="element-1310">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-1311">
            <button onClick={prevPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`} data-id="element-1312">
              Previous
            </button>
            <button onClick={nextPage} disabled={currentPage === totalPages} className={`ml-3 relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`} data-id="element-1313">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1314">
            <div data-id="element-1315">
              <p className="text-sm text-neutral-700" data-id="element-1316">
                Showing{' '}
                <span className="font-medium" data-id="element-1317">{indexOfFirstClient + 1}</span> to{' '}
                <span className="font-medium" data-id="element-1318">
                  {Math.min(indexOfLastClient, clients.length)}
                </span>{' '}
                of <span className="font-medium" data-id="element-1319">{clients.length}</span> clients
              </p>
            </div>
            <div data-id="element-1320">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1321">
                <button onClick={prevPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-500 hover:bg-neutral-50'}`} data-id="element-1322">
                  <span className="sr-only" data-id="element-1323">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-1324" />
                </button>
                {/* Page numbers */}
                {[...Array(totalPages).keys()].map(number => <button key={number + 1} onClick={() => paginate(number + 1)} className={`relative inline-flex items-center px-4 py-2 border border-neutral-300 bg-white text-sm font-medium ${currentPage === number + 1 ? 'bg-primary-50 text-primary-600 border-primary-500 z-10' : 'text-neutral-500 hover:bg-neutral-50'}`} data-id="element-1325">
                    {number + 1}
                  </button>)}
                <button onClick={nextPage} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-500 hover:bg-neutral-50'}`} data-id="element-1326">
                  <span className="sr-only" data-id="element-1327">Next</span>
                  <ChevronRightIcon className="h-5 w-5" data-id="element-1328" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>;
}